<template>
  <div>
    <h2>{{ title }}</h2>

    <b-card id="edit-station-card">

      <b-button v-if="$route.params.id && !deleted && !finished" :to="'/station/'+station.identifier" class="btn-sm" style=""><span class="fa fa-arrow-left"></span> Back</b-button>

      <hr v-if="$route.params.id && !deleted && !finished">

      <div v-if="!$route.params.id && !finished && !errorSaving" class="alert alert-info">
        Please provide at least the name, address, and postcode. Your contribution will need to be approved before it is visible. Thanks for helping!
      </div>
      <div v-if="$route.params.id && !finished && !deleted && !errorSaving" class="alert alert-info">
        Please make any corrections and additions to the information held about this station below.  Your contribution will need to be approved before it is visible. Thanks for helping! <br><br> <strong>For price and/or availability updates please use the button or leave a comment on the previous screen. This page is for long-term information updates.</strong>
      </div>

      <div v-if="finished" class="alert alert-success">
        Thanks for contributing information! Your help is much appreciated. We will make your edits available publicly as soon as possible.<br><br>
        <router-link to="/"><b-button class="btn btn-success">Finished</b-button></router-link>
      </div>

      <div v-if="errorSaving" class="alert alert-danger">
        {{errorSaving}}
      </div>

      <div v-if="$route.params.id && errorDeleting" class="alert alert-danger">
        There has been an marking the station for removal. Please try again.
      </div>

      <div v-if="$route.params.id && deleted" class="alert alert-success">
        Thanks for marking this station for removal. Once approved, this station will be removed.<br><br>
        <router-link to="/"><b-button class="btn btn-success">Finished</b-button></router-link>
      </div>

      <b-form v-if="!finished && !deleted">

        <div v-if="station.passwordRequired" class="alert alert-warning">
            <b-form-group label="Editing this station is locked for everyone but the owner. If you are the owner of this station, please provide your password.">
                <b-form-input v-model="station.password"></b-form-input>
            </b-form-group>
        </div>

        <b-form-group label="Name">
          <b-form-input required v-model="station.name"></b-form-input>
        </b-form-group>

        <b-form-group label="Address">
          <b-form-input required v-model="station.address"></b-form-input>
        </b-form-group>

        <b-form-group label="Postcode">
          <b-form-input required v-model="station.postcode"></b-form-input>
        </b-form-group>

        <b-form-group label="Available Connectors">
          <b-form-checkbox-group
                  v-model="station.connectors"
                  :options="['bayonet', 'acme', 'euroconnector', 'dish']"
          ></b-form-checkbox-group>
        </b-form-group>

        <b-form-group label="Phone">
          <b-form-input v-model="station.phone"></b-form-input>
        </b-form-group>

        <b-form-group label="Website">
          <b-form-input v-model="station.website" type="url"></b-form-input>
        </b-form-group>

        <b-form-group label="Payment Methods">
          <b-form-checkbox-group
                  v-model="station.payment_methods"
                  :options="['cash', 'card', 'account']"
          ></b-form-checkbox-group>
        </b-form-group>

        <div v-if="$route.params.id">
          <h4>Location</h4>
          <div class="alert alert-info">
            If the station is not marked in the correct location, please drag the pin to the correct location.
          </div>
          <l-map class="setLocationMap"
                  ref="setLocationMap"
                  id="setLocationMap"
                  :zoom="zoom"
                  :center="center"
                  @update:zoom="zoomUpdated"
                  @update:center="centerUpdated"
                  @update:bounds="boundsUpdated"
          >
            <l-tile-layer :url="url"></l-tile-layer>
          </l-map>
          <br><br>
        </div>

        <b-modal v-model="showLocationConfirmModal" @ok="setLocation" @hidden="resetLocation">
          <div class="d-block text-center">
            <h4>Are you sure you want to update the location of this station?</h4>
          </div>
        </b-modal>

        <opening-hours :opening-hours.sync="openingHours"></opening-hours>
        <br><br>

        <b-form-group label="Station Notes/Comments (Visible to all)">

        <div v-if="$route.params.id && !finished && !deleted && !errorSaving" class="alert alert-warning">
            If you are updating current availability or price, please do a price/availability update or comment on the previous screen. This page is for long-term information updates.
        </div>

          <b-form-textarea rows="5" v-model="station.notes"></b-form-textarea>
        </b-form-group>

        <br><br>
        <b-button class="pull-right btn-success" @click="saveStation">Save</b-button>
      </b-form>

      <br><br><hr v-if="$route.params.id && !deleted && !finished"><br><br>

      <details v-if="$route.params.id && !deleted && !finished">
        <summary class="text-danger">Is this station permanently closed or no longer offers LPG/Autogas?</summary>
        <div class="alert alert-danger">
          <b-form-checkbox v-model="confirmDelete">
            This station is permanently closed or has permanently stopped selling LPG/Autogas.
          </b-form-checkbox><br>
            <b-form-group label="Please explain how you know this station is permanently closed">
                <b-form-textarea v-model="station.marked_for_deletion_reason"></b-form-textarea>
            </b-form-group>
          <b-button class="btn btn-danger btn-sm" :disabled="!confirmDelete" @click="markForRemoval">Mark this station for removal</b-button>
        </div>
      </details>
    </b-card>
  </div>
</template>

<script>
  import Vue from 'vue'
  import {getTimeForDay} from '../utils.js'
  import OpeningHours from "../components/OpeningHours";
  import Leaflet from 'leaflet';

  export default {
    components: {OpeningHours},
    mounted() {
      if (this.$route.params.id) {
        this.title = 'Edit Station';
        Vue.axios.get('/api/v1.0/stations/' + this.$route.params.id).then((response) => {
          this.finished = false;
          if (response.data.data) {
            this.station = response.data.data;
          }
          if(this.station.payment_methods === null){
            this.station.payment_methods = [];
          }
          if(this.station.connectors === null){
            this.station.connectors = [];
          }

          let mapObject = this.$refs.setLocationMap.mapObject;

          let stationMarker = L.ExtraMarkers.icon({
            icon: 'fa-gas-pump',
            markerColor: 'red',
            shape: 'square',
            prefix: 'fa'
          });

          mapObject.setView(new Leaflet.LatLng(this.station.location.y, this.station.location.x), 16);

          this.marker = Leaflet.marker([this.station.location.y, this.station.location.x], {icon: stationMarker, draggable: true});

          this.marker.on('dragend', function(e){
            let marker = e.target;
            let position = marker.getLatLng();
            mapObject.panTo(new Leaflet.LatLng(position.lat, position.lng));
            this.showLocationConfirmModal = true;
            this.newLocation.y = position.lat;
            this.newLocation.x = position.lng;
          }.bind(this));

          mapObject.addLayer(this.marker);

          if (response.data.data.opening_hours) {
            let days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            for (let i = 0; i < days.length; i++) {
              if(this.station.opening_hours[days[i].substring(0, 3).toLocaleLowerCase()]){
                this.openingHours[days[i]] = {
                  openHour: this.station.opening_hours[days[i].substring(0, 3).toLocaleLowerCase()][0].opens.split(':')[0],
                  openMinutes: this.station.opening_hours[days[i].substring(0, 3).toLocaleLowerCase()][0].opens.split(':')[1],
                  closeHour: this.station.opening_hours[days[i].substring(0, 3).toLocaleLowerCase()][0].closes.split(':')[0],
                  closeMinutes: this.station.opening_hours[days[i].substring(0, 3).toLocaleLowerCase()][0].closes.split(':')[1]
                };
              }
            }
          }
        });
      } else {
        this.title = 'New Station';
      }
    },
    data() {
      return {
        title: 'New Station',
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        zoom: 6,
        center: [54.68653, -3.14209],
        bounds: null,
        finished: false,
        errorSaving: false,
        confirmDelete: false,
        deleted: false,
        errorDeleting: false,
        newLocation: {x: null, y: null},
        marker: null,
        showLocationConfirmModal: false,
        station: {
          active: false,
          connectors: [],
          opening_hours: [],
          payment_methods: [],
          marked_for_deletion_reason: '',
          notes: '',
          password: '',
          passwordRequired: '',
        },
        openingHours: {
          "Monday": {
            openHour: null,
            openMinutes: null,
            closeHour: null,
            closeMinutes: null
          },
          "Tuesday": {
            openHour: null,
            openMinutes: null,
            closeHour: null,
            closeMinutes: null
          },
          "Wednesday": {
            openHour: null,
            openMinutes: null,
            closeHour: null,
            closeMinutes: null
          },
          "Thursday": {
            openHour: null,
            openMinutes: null,
            closeHour: null,
            closeMinutes: null
          },
          "Friday": {
            openHour: null,
            openMinutes: null,
            closeHour: null,
            closeMinutes: null
          },
          "Saturday": {
            openHour: null,
            openMinutes: null,
            closeHour: null,
            closeMinutes: null
          },
          "Sunday": {
            openHour: null,
            openMinutes: null,
            closeHour: null,
            closeMinutes: null
          },
        }
      };
    },
    methods: {
      saveStation() {
        let openingHours = {};

        if (getTimeForDay(this.openingHours.Monday, 'opens')) {
          openingHours["mon"] = [{
            "opens": getTimeForDay(this.openingHours.Monday, 'opens'),
            "closes": getTimeForDay(this.openingHours.Monday, 'closes')
          }]
        }
        if (getTimeForDay(this.openingHours.Tuesday, 'opens')) {
          openingHours["tue"] = [{
            "opens": getTimeForDay(this.openingHours.Tuesday, 'opens'),
            "closes": getTimeForDay(this.openingHours.Tuesday, 'closes')
          }]
        }
        if (getTimeForDay(this.openingHours.Wednesday, 'opens')) {
          openingHours["wed"] = [{
            "opens": getTimeForDay(this.openingHours.Wednesday, 'opens'),
            "closes": getTimeForDay(this.openingHours.Wednesday, 'closes')
          }]
        }
        if (getTimeForDay(this.openingHours.Thursday, 'opens')) {
          openingHours["thu"] = [{
            "opens": getTimeForDay(this.openingHours.Thursday, 'opens'),
            "closes": getTimeForDay(this.openingHours.Thursday, 'closes')
          }]
        }
        if (getTimeForDay(this.openingHours.Friday, 'opens')) {
          openingHours["fri"] = [{
            "opens": getTimeForDay(this.openingHours.Friday, 'opens'),
            "closes": getTimeForDay(this.openingHours.Friday, 'closes')
          }]
        }
        if (getTimeForDay(this.openingHours.Saturday, 'opens')) {
          openingHours["sat"] = [{
            "opens": getTimeForDay(this.openingHours.Saturday, 'opens'),
            "closes": getTimeForDay(this.openingHours.Saturday, 'closes')
          }]
        }
        if (getTimeForDay(this.openingHours.Sunday, 'opens')) {
          openingHours["sun"] = [{
            "opens": getTimeForDay(this.openingHours.Sunday, 'opens'),
            "closes": getTimeForDay(this.openingHours.Sunday, 'closes')
          }]
        }

        if (openingHours.length < 1) {
          openingHours = null;
        }

        if (typeof this.station.connectors === 'string') {
          this.station.connectors = [this.station.connectors];
        }

        if (typeof this.station.payment_methods === 'string') {
          this.station.payment_methods = [this.station.payment_methods];
        }

        let data = {
          "name": this.station.name,
          "address": this.station.address,
          "postcode": this.station.postcode,
          "connectors": this.station.connectors || [],
          "opening_hours": openingHours,
          "phone": this.station.phone,
          "website": this.station.website,
          "payment_methods": this.station.payment_methods || [],
          "notes": this.station.notes,
          "password": this.station.password
        };

        if(this.$route.params.id){
          data["location"] = {};
          data["location"]["x"] = this.station.location.x;
          data["location"]["y"] = this.station.location.y;
        }

        if (this.station.id) {
          Vue.axios.put('/api/v1.0/stations/' + this.station.id, {
            "data": data
          }).then((response) => {
            if (response.data.data.id) {
              this.finished = true;
              this.errorSaving = false;
            } else {
              this.errorSaving = "There has been an error saving your contributions. Please try again. Please provide at least the name, address, and postcode.";
            }
            window.scrollTo(0, 0);
          }).catch((error) => {
            try{
              if(error.response.status === 401) {
                  this.errorSaving = "This station is locked and can only be updated by the owner. The password provided is not correct.";
              }else if(error.response.data.error.isJoi && error.response.data.error.details[0].path.includes("opening_hours")){
                this.errorSaving = "There was an error saving your contributions. Please ensure the opening hours are all in the correct 24 hour format.";
              }else if(error.response.data.error.isJoi && error.response.data.error.details[0].path.includes("website")){
                  this.errorSaving = "There was an error saving your contributions. Please ensure the website URL is correctly formed and starts with http:// or https://";
              }else{
                this.errorSaving = "There has been an error saving your contributions. Please try again. Please provide at least the name, address, and postcode.";
              }
            }catch(e){
              this.errorSaving = "There has been an error saving your contributions. Please ensure you are connected to the internet and try again.";
            }
            window.scrollTo(0, 0);
          });
        } else {
          Vue.axios.post('/api/v1.0/stations', {
            "data": data
          }).then((response) => {
            if (response.data.data.id) {
              this.finished = true;
              this.errorSaving = false;
            } else {
              this.errorSaving = "There has been an error saving your contributions. Please try again. Please provide at least the name, address, and postcode.";
            }
            window.scrollTo(0, 0);
          }).catch(() => {
            try{
              if(error.response.data.error.isJoi && error.response.data.error.details[0].path.includes("opening_hours")){
                this.errorSaving = "There was an error saving your contributions. Please ensure the opening hours are all in the correct 24 hour format.";
              }else{
                this.errorSaving = "There has been an error saving your contributions. Please try again. Please provide at least the name, address, and postcode.";
              }
            }catch(e){
              this.errorSaving = "There has been an error saving your contributions. Please ensure you are connected to the internet and try again.";
            }
            window.scrollTo(0, 0);
          });
        }
      },
      markForRemoval() {
        if (this.station.id) {
          Vue.axios.delete('/api/v1.0/stations/' + this.station.id, {
              data: {marked_for_deletion_reason: this.station.marked_for_deletion_reason}
          }).then((response) => {
            if (response.data.data.id) {
              this.deleted = true;
              this.errorDeleting = false;
            } else {
              this.deleted = false;
              this.errorDeleting = true;
            }
            window.scrollTo(0, 0);
          }).catch(() => {
            this.errorDeleting = true;
            window.scrollTo(0, 0);
          });
        }
      },
      zoomUpdated (zoom) {
        this.zoom = zoom;
      },
      centerUpdated (center) {
        this.center = center;
      },
      boundsUpdated (bounds) {
        this.bounds = bounds;
      },
      setLocation (){
        this.station.location = this.newLocation;
      },
      resetLocation (){
        let mapObject = this.$refs.setLocationMap.mapObject;
        mapObject.panTo(new Leaflet.LatLng(this.station.location.y, this.station.location.x));
        this.marker.setLatLng(new Leaflet.LatLng(this.station.location.y, this.station.location.x));
        this.newLocation = {x: null, y: null};
      }
    }
  };
</script>

<style scoped>
  @media only screen and (max-width: 750px) {
    #edit-station-card {
      width: 100%;
      margin: 0 auto 60mm;
      padding: 0;
    }
  }
  .setLocationMap {
    height:300px;
  }
</style>

<style>
  @import '~leaflet.locatecontrol/dist/L.Control.Locate.css';
  @import '~leaflet-extra-markers/src/assets/css/leaflet.extra-markers.css';
</style>